import {
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { DynamicDialogRef } from '../../../../core/services/dialog';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { CloseIcon } from '../../Icons/ic-close/ic-close';
import { IcSearchComponent } from '../../Icons/ic-search/ic-search';

import {
  GlobalSearchResult,
  GlobalSearchRow,
  GlobalSearchService,
} from '../../../../core/services/global-search/global-search.service';
import { ApiResponse } from '../../../../shared/models/common.model';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { EmptyStateComponent } from '../../empty-state/empty-state.component';

/**
 * GlobalSearch component.
 * Provides UI presentation and event handling for global search.
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [IcSearchComponent, CloseIcon, TranslatePipe, EmptyStateComponent],
  templateUrl: './global-search.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './global-search.scss',
})
export class GlobalSearch implements OnInit {
  /** Property search results. */
  searchResults = signal<GlobalSearchRow[]>([]);
  /** Property search query. */
  searchQuery = '';
  /** Property loading. */
  loading = signal<boolean>(false);
  /** Property search$. */
  search$ = new Subject<string>();

  /** Injected service for global search service. */
  private readonly globalSearchService = inject(GlobalSearchService);
  /** Injected service for router. */
  private readonly router = inject(Router);
  /** Injected service for ref. */
  ref = inject(DynamicDialogRef);

  /**
   * Reference to the search input element or component.
   */
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  /**
   * Lifecycle hook that runs on component initialization.
   */
  ngOnInit() {
    this.search$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((query) => {
        this.searchQuery = query;
        this.getSearchResult();
      });

    setTimeout(() => this.searchInput?.nativeElement.focus(), 300);
  }

  /** 🔍 Fetch search results */
  getSearchResult(): void {
    if (!this.searchQuery.trim()) {
      this.searchResults.set([]);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.globalSearchService.search({ q: this.searchQuery }).subscribe({
      next: (res: ApiResponse<GlobalSearchResult>) => {
        if (res.success && res.data) {
          this.searchResults.set(res.data.result);
        } else {
          this.searchResults.set([]);
        }
        this.loading.set(false);
      },
      error: () => {
        this.searchResults.set([]);
        this.loading.set(false);
      },
    });
  }

  /** 🔹 Handle typing in search box */
  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    this.search$.next(value);

    if (!value) {
      this.searchResults.set([]);
      this.loading.set(false);
    }
  }

  /** 🔹 Handle submit (Enter key or button) */
  onSearchSubmit(): void {
    this.getSearchResult();
  }

  /** 🔹 When user selects a result */
  onSearchResultSelect(result: GlobalSearchRow): void {
    void this.navigateTo(result);
    this.ref.close({ success: true }); // ✅ close dialog
  }

  /** 🔹 Normalize source_table to canonical form */
  normalizeSourceTable(source: string): string {
    const s = (source || '').toLowerCase().trim().replace(/[- ]/g, '_');
    const map: Record<string, string> = {
      declaration_items: 'declaration_items',
      declarationitems: 'declaration_items',
      delivery_items: 'delivery_items',
      deliveryitems: 'delivery_items',
      users: 'users',
      user: 'users',
      declarations: 'declarations',
      declaration: 'declarations',
      deliveries: 'deliveries',
      delivery: 'deliveries',
      shipments: 'shipments',
      shipment: 'shipments',
      transit_declarations: 'transit_declarations',
      transit_declaration: 'transit_declarations',
      transit_arrival: 'transit_arrival',
      transitarrival: 'transit_arrival',
      transit_departure: 'transit_departure',
      transitdeparture: 'transit_departure',
      transit: 'transit_declarations',
      ncts: 'transit_declarations',
      emcs: 'emcs',
      excise_movements: 'emcs',
      excisemovements: 'emcs',
      tickets: 'tickets',
      ticket: 'tickets',
      rto: 'rto',
      rto_shipments: 'rto',
      rtoshipments: 'rto',
      brokers: 'brokers',
      broker: 'brokers',
      clients: 'clients',
      client: 'clients',
      parties: 'parties',
      party: 'parties',
      roles: 'roles',
      role: 'roles',
    };
    return map[s] ?? s;
  }

  /** 🔹 Format source table string for display badge */
  formatSourceTable(source: string): string {
    if (!source) return '';
    return source.replace(/[-_]/g, ' ');
  }

  /** 🔹 Navigate based on result type */
  async navigateTo(result: GlobalSearchRow): Promise<boolean> {
    const rowRecord = result as unknown as Record<string, unknown>;
    const id =
      result.record_id ??
      (rowRecord['recordId'] as number | string | undefined) ??
      (result.id?.includes(':') ? result.id.split(':')[1] : result.id);
    const shipmentId =
      result.shipment_id ??
      (rowRecord['shipmentId'] as number | string | null | undefined);
    const source = this.normalizeSourceTable(result.source_table);

    switch (source) {
      case 'shipments':
        if (!id) return false;
        return this.router.navigate(['/shipments/details', id, 'info'], {
          queryParams: { tabMethod: 'true' },
        });

      case 'declarations':
        if (!id) return false;
        return this.router.navigate(['/declarations/details', id, 'info'], {
          queryParams: { tabMethod: 'true' },
        });

      case 'deliveries': {
        const targetShipmentId = shipmentId ?? id;
        if (!targetShipmentId) return false;
        return this.router.navigate(
          ['/shipments/details', targetShipmentId, 'info'],
          {
            queryParams: { tabMethod: 'true' },
          }
        );
      }

      case 'declaration_items': {
        const targetShipmentId = shipmentId ?? id;
        if (!targetShipmentId) return false;
        return this.router.navigate(
          ['/shipments/details', targetShipmentId, 'declaration'],
          {
            queryParams: { tabMethod: 'true' },
          }
        );
      }

      case 'delivery_items': {
        const targetShipmentId = shipmentId ?? id;
        if (!targetShipmentId) return false;
        return this.router.navigate(
          ['/shipments/details', targetShipmentId, 'compliance'],
          {
            queryParams: { tabMethod: 'true' },
          }
        );
      }

      case 'users':
        if (!id) return false;
        return this.router.navigate(['/settings/users/details', id]);

      case 'transit_arrival':
        if (!id) return false;
        return this.router.navigate([
          '/transit-declarations/transit-arrival/details',
          id,
        ]);

      case 'transit_departure':
      case 'transit_declarations':
        if (!id) return false;
        return this.router.navigate([
          '/transit-declarations/transit-departure/details',
          id,
        ]);

      case 'emcs':
        if (!id) return false;
        return this.router.navigate(['/emcs/details', id]);

      case 'tickets':
        if (!id) return false;
        return this.router.navigate(['/tickets/details', id]);

      case 'rto':
        if (!id) return false;
        return this.router.navigate([
          '/rto/shipments/details',
          id,
          'overview',
        ]);

      case 'brokers':
        if (!id) return false;
        return this.router.navigate(['/settings/brokers/details', id, 'info']);

      case 'clients':
        if (!id) return false;
        return this.router.navigate(['/settings/clients/details', id, 'info']);

      case 'parties':
        if (!id) return false;
        return this.router.navigate(['/settings/parties/details', id, 'info']);

      case 'roles':
        if (!id) return false;
        return this.router.navigate(['/settings/roles/edit', id]);

      default:
        return false;
    }
  }

  /** 🔹 Clear the search box */
  clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchQuery = '';
    this.searchResults.set([]);
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  /** 🔹 Close dialog manually */
  closeDialog(): void {
    this.ref.close({ success: true });
  }
}
